/**
 * Loading a remote's expose, the one way every host does it.
 *
 * Wraps the module-level `registerRemotes` / `loadRemote` of
 * `@module-federation/enhanced/runtime` - the build plugin's instance, the
 * only one wired to the host's shared modules; never `createInstance`. A
 * federation name is declared against one entry for the life of the page. A
 * load that fails reports the remote down, so the heartbeat watches it; the
 * next load after that re-registers the SAME entry with `force`, which is
 * safe because nothing of the failed load was evaluated, and is what makes
 * the runtime fetch the entry again rather than replay its failure.
 */
import type { Signal } from '@crown/signal';
import { isFunction } from '../ir/guards';
import {
  createRemoteStatus,
  type RemoteState,
  type RemoteStatusStore,
  type RemoteTarget,
} from './status';

export interface FederationRuntimeLike {
  registerRemotes(
    remotes: { name: string; entry: string }[],
    options?: { force?: boolean },
  ): void;
  /** A remote's expose: a module namespace, or nothing when the remote exposes nothing there. */
  loadRemote(id: string): Promise<object | null | undefined>;
  /** The build plugin's instance, when the page has one; a page without starts one with `init`. */
  getInstance?(): object | null | undefined;
  init?(options: {
    name: string;
    remotes: { name: string; entry: string }[];
    shareStrategy?: 'loaded-first' | 'version-first';
  }): object;
}

export class RemoteNameConflictError extends Error {
  override readonly name = 'RemoteNameConflictError';
  constructor(
    readonly remoteName: string,
    readonly registeredEntry: string,
    readonly requestedEntry: string,
  ) {
    super(
      `Federation name "${remoteName}" is declared against ${registeredEntry} and cannot be pointed at ${requestedEntry}. A name maps to one entry for the life of the page.`,
    );
  }
}

export class RemoteUnavailableError extends Error {
  override readonly name = 'RemoteUnavailableError';
  constructor(
    readonly remoteName: string,
    readonly expose: string,
    override readonly cause: unknown,
    /** The remote answered: its expose failed, not the connection. */
    readonly reachable = false,
  ) {
    super(
      `${remoteName}${expose.replace(/^\./, '')} could not be loaded: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  }
}

type RuntimeSource =
  | FederationRuntimeLike
  | (() => Promise<FederationRuntimeLike>);

interface Declared {
  readonly target: RemoteTarget;
  registered: boolean;
  /** A load of it failed, so the next registration forces a fresh fetch. */
  failed: boolean;
}

const moduleRuntime = (): Promise<FederationRuntimeLike> =>
  import('@module-federation/enhanced/runtime');

let source: RuntimeSource = moduleRuntime;
let resolved: Promise<FederationRuntimeLike> | null = null;
let status: RemoteStatusStore | null = null;
let hostName = 'crown_host';
const declared = new Map<string, Declared>();

function runtime(): Promise<FederationRuntimeLike> {
  resolved ??= isFunction(source) ? source() : Promise.resolve(source);
  return resolved;
}

export function federationStatus(): RemoteStatusStore {
  status ??= createRemoteStatus();
  return status;
}

/** Gives the page its runtime and status store; a test hands in its own. Call before the first load. */
export function configureFederation(options: {
  runtime?: RuntimeSource;
  status?: RemoteStatusStore;
  hostName?: string;
}): void {
  if (options.hostName) hostName = options.hostName;
  if (options.runtime) {
    source = options.runtime;
    resolved = null;
  }
  if (options.status) status = options.status;
}

/** Names a remote and where its manifest is. Declaring it again against the same entry is nothing. */
export function declareRemote(target: RemoteTarget): void {
  const existing = declared.get(target.name);
  if (existing) {
    if (existing.target.entry !== target.entry)
      throw new RemoteNameConflictError(
        target.name,
        existing.target.entry,
        target.entry,
      );
    return;
  }
  declared.set(target.name, { target, registered: false, failed: false });
}

export async function loadExpose<T>(name: string, expose: string): Promise<T> {
  const remote = declared.get(name);
  if (!remote)
    throw new Error(
      `${name} has not been declared; call declareRemote({ name, entry }) first`,
    );
  const store = federationStatus();
  store.watch(remote.target);
  try {
    const federation = await runtime();
    if (!remote.registered || remote.failed) {
      const entry = [{ name, entry: remote.target.entry }];
      // A page whose build did not start the runtime starts it here, once, and
      // loaded-first, as every host's build does: under version-first one
      // remote that is down holds up every other remote's load.
      if (
        federation.getInstance &&
        federation.init &&
        !federation.getInstance()
      )
        federation.init({
          name: hostName,
          remotes: entry,
          shareStrategy: 'loaded-first',
        });
      else
        federation.registerRemotes(
          entry,
          remote.failed ? { force: true } : undefined,
        );
      remote.registered = true;
      // Cleared here, before the load is awaited: a second view recovering at the same
      // moment must not force a second registration under the first one's load.
      remote.failed = false;
    }
    const module = await federation.loadRemote(
      `${name}/${expose.replace(/^\.\//, '')}`,
    );
    if (module === null || module === undefined)
      throw new Error(`${name} exposes nothing at ${expose}`);
    remote.failed = false;
    store.reportUp(name);
    // SAFETY: the expose's shape is the caller's contract with the remote; `T` names it.
    return module as T;
  } catch (cause) {
    remote.failed = true;
    // Unreachable, or reachable with an expose that failed? One probe says:
    // a remote that answers is up, and is not retried as if it were down.
    const state = await store.checkNow(name);
    if (state !== 'up') store.reportDown(remote.target);
    throw new RemoteUnavailableError(name, expose, cause, state === 'up');
  }
}

export function remoteStatus(name: string): Signal.State<RemoteState> {
  return federationStatus().state(name);
}

export function checkRemoteNow(name: string): Promise<RemoteState> {
  return federationStatus().checkNow(name);
}

export function resetFederationForTests(): void {
  status?.dispose();
  status = null;
  declared.clear();
  source = moduleRuntime;
  resolved = null;
  hostName = 'crown_host';
}
